/**
 * Appeal lifecycle stage vocabulary — shared by the Appeals v2 surfaces
 * (AppealsQueues, AppealDetail, AppealAdvanceModal).
 *
 * The canonical stage order mirrors the backend advance state-machine. The
 * timeline renders every stage in this order; the advance modal offers the
 * stages that come *after* the current one (plus the `withdrawn` side-exit).
 * `withdrawn` is a terminal side-exit and is intentionally NOT part of the
 * linear order.
 */

export const STAGE_ORDER = [
  'value_notice_received',
  'appeal_decision_pending',
  'protest_filed',
  'hearing_notice_received',
  'hearing_scheduled',
  'hearing_resolved',
  'awaiting_board_order',
  'board_order_received',
  'litigation_window',
  'litigation_filed',
  'litigation_closed',
  'invoiced',
  'paid',
] as const;

export type AppealStage = (typeof STAGE_ORDER)[number] | 'withdrawn';

const STAGE_LABELS: Record<string, string> = {
  value_notice_received: 'Value notice received',
  appeal_decision_pending: 'Appeal decision pending',
  protest_filed: 'Protest filed',
  hearing_notice_received: 'Hearing notice received',
  hearing_scheduled: 'Hearing scheduled',
  hearing_resolved: 'Hearing resolved',
  awaiting_board_order: 'Awaiting board order',
  board_order_received: 'Board order received',
  litigation_window: 'Litigation window',
  litigation_filed: 'Litigation filed',
  litigation_closed: 'Litigation closed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  withdrawn: 'Withdrawn',
};

/** Humanize a stage key; falls back to a generic prettifier for unknowns. */
export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return '—';
  return STAGE_LABELS[stage] ?? stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * The stages an appeal may legally advance to from `current`: every stage
 * after it in the canonical order, plus `withdrawn` (the terminal side-exit).
 * Illegal picks are still ultimately rejected by the server (400).
 */
export function nextStages(current: string | null | undefined): string[] {
  const idx = current ? STAGE_ORDER.indexOf(current as (typeof STAGE_ORDER)[number]) : -1;
  const forward = idx >= 0 ? STAGE_ORDER.slice(idx + 1) : [...STAGE_ORDER];
  const out: string[] = [...forward];
  if (current !== 'withdrawn') out.push('withdrawn');
  return out;
}

/**
 * The stages from which an appeal cannot move again — the mirror of
 * `TERMINAL_APPEAL_STAGES` in `src/config/enums.js`, which is what `fileAppeal`
 * refuses to reopen. Anything else is an appeal still in flight.
 *
 * ⚠️ THE SERVER CARRIES A SECOND, NARROWER DEFINITION and this is not it. The
 * stage-template impact endpoint counts an "open appeal" as `NOT IN ('paid',
 * 'withdrawn')` — it treats `invoiced` as still open. The two disagree by design
 * (billing has started vs. the work is finished) and both predate this file; do not
 * quietly reconcile them here. Use THIS one for "can an operator still act on this
 * appeal", which is what the parcel header asks.
 */
export const TERMINAL_STAGES: ReadonlySet<string> = new Set(['paid', 'withdrawn', 'invoiced']);

/** Is this appeal finished? An unknown/absent stage is treated as still in flight. */
export function isTerminalStage(stage: string | null | undefined): boolean {
  return !!stage && TERMINAL_STAGES.has(stage);
}

/** Which appeal date field (if any) is meaningful once a stage is reached. */
export const STAGE_DATE_FIELD: Record<string, string> = {
  hearing_scheduled: 'hearing_date',
  awaiting_board_order: 'board_order_expected_date',
  board_order_received: 'board_order_received_date',
  litigation_window: 'litigation_deadline_date',
};

/** Badge variant for a lifecycle stage pill. */
export function stageVariant(stage: string | null | undefined): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (!stage) return 'neutral';
  if (stage === 'withdrawn') return 'neutral';
  if (stage === 'paid') return 'success';
  if (stage === 'invoiced' || stage === 'litigation_closed') return 'success';
  if (stage.startsWith('litigation')) return 'warning';
  return 'info';
}
